'use client';

import type { CustomerRatingsHistogramFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { useId } from 'react';
import { useRouter } from 'next/navigation';
import { CustomerRatingMeter } from '../CustomerRatingMeter/CustomerRatingMeter';
import { CustomerRatingMeterLabel } from '../CustomerRatingMeterLabel/CustomerRatingMeterLabel.client';
import { useCustomerRatingsAndReviewPage } from '../../../CustomerRatingsAndReviewsPage/hooks/useCustomerRatingsAndReviewPage/useCustomerRatingsAndReviewPage';
import { useCustomerRatingsTracking } from '../../../Tracking/hooks/useCustomerRatingsTracking/useCustomerRatingsTracking';
import { CustomerRatingsAndReviewsPageSearchParams } from '../../../CustomerRatingsAndReviewsPage/types';
import { CustomerRatingMeterCount } from '../CustomerRatingMeterCount/CustomerRatingMeterCount';
import { useCustomerRatingsHistogramMessages } from '../useCustomerRatingsHistogramMessages/useCustomerRatingsHistogramMessages';

export type CustomerRatingsHistogramType = 'unlinked' | 'linked' | 'linkedWithSelectedIndicator';

export type CustomerRatingsHistogramMultiSelectProps = {
  reviewStatistics: CustomerRatingsHistogramFragmentFragment['reviewStatistics'];
};
/**
 * The CustomerRatingsHistogramMultiSelect component displays a visual histogram of how many ratings are available for a specific rating.
 * It uses multi-select functionality to allow users to filter ratings.
 *
 * @param reviewStatistics - The review statistics data for the histogram
 * @returns A React component rendering the customer ratings histogram with multi-select functionality
 */
export const CustomerRatingsHistogramMultiSelect = ({
  reviewStatistics,
}: CustomerRatingsHistogramMultiSelectProps) => {
  const { getUrl, getArticleId, getSearchParamValue } = useCustomerRatingsAndReviewPage();
  const { trackFilterByRatingValue } = useCustomerRatingsTracking();
  const { push } = useRouter();
  const { getLinkLabel } = useCustomerRatingsHistogramMessages();
  const id = useId();

  const articleId = getArticleId();

  if (articleId === null) {
    return null;
  }

  const ratingParamValues =
    getSearchParamValue(CustomerRatingsAndReviewsPageSearchParams.FILTER_BY_RATING)?.split(',') ||
    [];

  return (
    <div className="grid grid-cols-[auto_auto_1fr_auto]">
      {reviewStatistics.map(({ rating, portion, count }, index) => {
        const isDisabled = count === 0;
        const isActive = isDisabled ? false : ratingParamValues.includes(rating.toString());

        const handleOnChange = () => {
          const url = getUrl({
            articleId,
            rating: isActive
              ? // if the filter is active, remove it from the list
                ratingParamValues.filter((param) => param !== rating.toString()).join(',')
              : // otherwise add it to the list
                ratingParamValues.concat(rating.toString()).join(','),
          });

          trackFilterByRatingValue(rating);
          push(url);
        };
        const checkboxId = id + rating.toString();

        return (
          <div
            className="col-span-4 grid cursor-pointer grid-cols-subgrid items-center"
            // eslint-disable-next-line react/no-array-index-key
            key={`customerRatingHistogramMultiSelect-${index}`}
          >
            <div className="-ml-1">
              <Checkbox
                checked={isActive}
                disabled={isDisabled}
                onChange={handleOnChange}
                id={checkboxId}
                className="cursor-pointer"
              />
              <label htmlFor={checkboxId} className="sr-only">
                {getLinkLabel(rating, count)}
              </label>
            </div>
            <div
              className="col-span-3 grid grid-cols-subgrid items-center gap-x-1.5"
              aria-hidden="true"
              onClick={isDisabled ? undefined : handleOnChange}
            >
              <CustomerRatingMeterLabel rating={rating} />
              <CustomerRatingMeter portion={portion} />
              <CustomerRatingMeterCount ratingsCount={count} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
